import type {
  TotalAssetValueDto,
  AssetValueTrendDto,
  RoiMetricsDto,
  CostBreakdownDto,
  DepreciationByCategoryDto,
  AssetValueByLocationDto,
  TopAssetsByValueDto,
  WarrantyStatusDto,
  FinancialSummaryDto,
  ChartSummaryDto,
} from '@/types/financial-widgets';
import { getModuleMode } from '@/lib/feature-mode';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getApiUrl } from '@/lib/api-config';

// Helper function to get current data source mode from feature settings
const getDataSource = (): 'demo' | 'api' => {
  const mode = getModuleMode('dashboard');
  if (mode === 'demo') return 'demo';
  return 'api';
};

// Mock data for demo mode
const mockTotalAssetValue: TotalAssetValueDto = {
  totalValue: 2450000,
  changeAmount: 76000,
  changePercentage: 3.2,
  trendDirection: 'up',
  totalAssets: 156,
};

const generateMockAssetValueTrend = (months: number): AssetValueTrendDto => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentDate = new Date();
  const trendData: AssetValueTrendDto['trendData'] = [];
  
  for (let i = months - 1; i >= 0; i--) {
    const date = new Date(currentDate);
    date.setMonth(date.getMonth() - i);
    const baseValue = 2450000 - (i * 20000);
    trendData.push({
      month: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
      value: Math.round(baseValue + Math.random() * 50000),
      depreciation: Math.round(15000 + Math.random() * 5000),
    });
  }
  
  const currentValue = trendData[trendData.length - 1]?.value ?? 2450000;
  const previousValue = trendData[trendData.length - 2]?.value ?? 2400000;
  const changePercentage = previousValue > 0 ? ((currentValue - previousValue) / previousValue) * 100 : 0;
  
  return {
    trendData,
    currentValue,
    previousValue,
    changePercentage: Math.round(changePercentage * 100) / 100,
  };
};

const mockRoiMetrics: RoiMetricsDto = {
  totalROI: -12.5,
  averageROI: -8.3,
  totalInvestment: 2800000,
  totalReturns: 2450000,
  assetsWithROI: 156,
  categoryROIs: [
    { category: 'IT Equipment', roi: -15.2, investment: 850000 },
    { category: 'Vehicles', roi: -22.5, investment: 650000 },
    { category: 'Furniture', roi: -8.0, investment: 320000 },
    { category: 'Machinery', roi: -5.5, investment: 580000 },
    { category: 'Office Equipment', roi: -10.0, investment: 400000 },
  ],
};

const mockCostBreakdown: CostBreakdownDto = {
  totalCost: 2800000,
  categories: [
    { name: 'IT Equipment', amount: 850000, percentage: 30.4, assetCount: 45, color: '#3b82f6' },
    { name: 'Vehicles', amount: 650000, percentage: 23.2, assetCount: 12, color: '#f59e0b' },
    { name: 'Machinery', amount: 580000, percentage: 20.7, assetCount: 18, color: '#8b5cf6' },
    { name: 'Office Equipment', amount: 400000, percentage: 14.3, assetCount: 13, color: '#ec4899' },
    { name: 'Furniture', amount: 320000, percentage: 11.4, assetCount: 68, color: '#10b981' },
  ],
};

const mockDepreciationByCategory: DepreciationByCategoryDto = {
  totalDepreciation: 373000,
  categories: [
    { category: 'IT Equipment', ytdDepreciation: 42500, totalDepreciation: 129250, depreciationRate: 15.2, assetCount: 45 },
    { category: 'Vehicles', ytdDepreciation: 48750, totalDepreciation: 146250, depreciationRate: 22.5, assetCount: 12 },
    { category: 'Furniture', ytdDepreciation: 8000, totalDepreciation: 25600, depreciationRate: 8.0, assetCount: 68 },
    { category: 'Machinery', ytdDepreciation: 10150, totalDepreciation: 31900, depreciationRate: 5.5, assetCount: 18 },
    { category: 'Office Equipment', ytdDepreciation: 13333, totalDepreciation: 40000, depreciationRate: 10.0, assetCount: 13 },
  ],
};

const mockAssetValueByLocation: AssetValueByLocationDto = {
  totalValue: 2450000,
  locations: [
    { location: 'London HQ', value: 980000, assetCount: 62, percentage: 40.0 },
    { location: 'Manchester Office', value: 490000, assetCount: 35, percentage: 20.0 },
    { location: 'Birmingham Warehouse', value: 367500, assetCount: 28, percentage: 15.0 },
    { location: 'Edinburgh Branch', value: 318500, assetCount: 18, percentage: 13.0 },
    { location: 'Leeds Distribution', value: 294000, assetCount: 13, percentage: 12.0 },
  ],
};

const mockTopAssetsByValue: TopAssetsByValueDto = {
  assets: [
    { id: 1, name: 'CNC Milling Machine', category: 'Machinery', purchasePrice: 200000, currentValue: 185000, depreciationAmount: 15000, status: 'Active' },
    { id: 2, name: 'Company Vehicle Fleet - Van 1', category: 'Vehicles', purchasePrice: 50000, currentValue: 42000, depreciationAmount: 8000, status: 'Active' },
    { id: 3, name: 'Server Rack Infrastructure', category: 'IT Equipment', purchasePrice: 50000, currentValue: 38500, depreciationAmount: 11500, status: 'Active' },
    { id: 4, name: 'Industrial Printer', category: 'Machinery', purchasePrice: 40000, currentValue: 35000, depreciationAmount: 5000, status: 'Active' },
    { id: 5, name: 'Company Vehicle Fleet - Van 2', category: 'Vehicles', purchasePrice: 50000, currentValue: 38000, depreciationAmount: 12000, status: 'Active' },
    { id: 6, name: 'Conference Room AV System', category: 'IT Equipment', purchasePrice: 35000, currentValue: 28000, depreciationAmount: 7000, status: 'Active' },
    { id: 7, name: 'Warehouse Forklift', category: 'Machinery', purchasePrice: 50000, currentValue: 45000, depreciationAmount: 5000, status: 'Active' },
    { id: 8, name: 'Network Infrastructure', category: 'IT Equipment', purchasePrice: 40000, currentValue: 32000, depreciationAmount: 8000, status: 'Active' },
    { id: 9, name: 'Office Furniture Set - Floor 3', category: 'Furniture', purchasePrice: 20000, currentValue: 18000, depreciationAmount: 2000, status: 'Active' },
    { id: 10, name: 'Security System', category: 'IT Equipment', purchasePrice: 30000, currentValue: 25000, depreciationAmount: 5000, status: 'Active' },
  ],
  totalValue: 2450000,
  topAssetsPercentage: 19.8,
};

const mockWarrantyStatus: WarrantyStatusDto = {
  totalAssets: 156,
  activeWarranties: 89,
  expiredWarranties: 24,
  expiringWithin30Days: 8,
  expiringWithin90Days: 18,
  noWarranty: 25,
  categories: [
    { status: 'Active', count: 89, percentage: 57.1, color: '#10b981' },
    { status: 'Expiring Soon', count: 18, percentage: 11.5, color: '#f59e0b' },
    { status: 'Expired', count: 24, percentage: 15.4, color: '#ef4444' },
    { status: 'No Warranty', count: 25, percentage: 16.0, color: '#6b7280' },
  ],
};

const mockChartSummary: ChartSummaryDto = {
  byCategory: [
    { category: 'IT Equipment', netBookValue: 722500, count: 45 },
    { category: 'Vehicles', netBookValue: 503750, count: 12 },
    { category: 'Machinery', netBookValue: 548100, count: 18 },
    { category: 'Office Equipment', netBookValue: 360000, count: 13 },
    { category: 'Furniture', netBookValue: 294400, count: 68 },
  ],
  byLocation: [
    { location: 'London HQ', netBookValue: 980000, count: 62 },
    { location: 'Manchester Office', netBookValue: 490000, count: 35 },
    { location: 'Birmingham Warehouse', netBookValue: 367500, count: 28 },
    { location: 'Edinburgh Branch', netBookValue: 318500, count: 18 },
    { location: 'Leeds Distribution', netBookValue: 294000, count: 13 },
  ],
  byStatus: [
    { status: 'Active', displayName: 'Active', netBookValue: 2100000, count: 120 },
    { status: 'In Use', displayName: 'In Use', netBookValue: 200000, count: 15 },
    { status: 'Available', displayName: 'Available', netBookValue: 100000, count: 8 },
    { status: 'Maintenance', displayName: 'Maintenance', netBookValue: 40000, count: 6 },
    { status: 'Disposed', displayName: 'Disposed', netBookValue: 10000, count: 7 },
  ],
};

const mockFinancialSummary: FinancialSummaryDto = {
  totalAssetValue: 2450000,
  totalPurchasePrice: 2800000,
  totalDepreciation: 373000,
  ytdDepreciation: 122733,
  averageAssetValue: 15705,
  totalMaintenanceCost: 45000,
  roi: -12.5,
  totalAssets: 156,
  activeAssets: 144,
  disposedAssets: 12,
};

export class FinancialWidgetsService {
  private getBaseUrl(): string {
    const apiUrlFromConfig = getApiUrl();
    const backendUrl = getBackendUrl();
    const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');
    return isE2B ? backendUrl : (apiUrlFromConfig ?? backendUrl);
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const authConfig = getAuthConfig();
    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
    return headers;
  }

  async getTotalAssetValue(): Promise<TotalAssetValueDto> {
    if (getDataSource() === 'demo') {
      return mockTotalAssetValue;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/total-asset-value`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch total asset value');
    return response.json();
  }

  async getAssetValueTrend(months: number = 12): Promise<AssetValueTrendDto> {
    if (getDataSource() === 'demo') {
      return generateMockAssetValueTrend(months);
    }
    const headers = await this.buildHeaders();
    const params = new URLSearchParams({ months: months.toString() });
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/asset-value-trend?${params}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch asset value trend');
    return response.json();
  }

  async getRoiMetrics(): Promise<RoiMetricsDto> {
    if (getDataSource() === 'demo') {
      return mockRoiMetrics;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/roi-metrics`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch ROI metrics');
    return response.json();
  }

  async getCostBreakdown(): Promise<CostBreakdownDto> {
    if (getDataSource() === 'demo') {
      return mockCostBreakdown;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/cost-breakdown`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch cost breakdown');
    return response.json();
  }

  async getDepreciationByCategory(): Promise<DepreciationByCategoryDto> {
    if (getDataSource() === 'demo') {
      return mockDepreciationByCategory;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/depreciation-by-category`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch depreciation by category');
    return response.json();
  }

  async getAssetValueByLocation(): Promise<AssetValueByLocationDto> {
    if (getDataSource() === 'demo') {
      return mockAssetValueByLocation;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/asset-value-by-location`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch asset value by location');
    return response.json();
  }

  async getTopAssetsByValue(limit: number = 10): Promise<TopAssetsByValueDto> {
    if (getDataSource() === 'demo') {
      return {
        ...mockTopAssetsByValue,
        assets: mockTopAssetsByValue.assets.slice(0, limit),
      };
    }
    const headers = await this.buildHeaders();
    const params = new URLSearchParams({ limit: limit.toString() });
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/top-assets?${params}`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch top assets by value');
    return response.json();
  }

  async getWarrantyStatus(): Promise<WarrantyStatusDto> {
    if (getDataSource() === 'demo') {
      return mockWarrantyStatus;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/warranty-status`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch warranty status');
    return response.json();
  }

  async getFinancialSummary(): Promise<FinancialSummaryDto> {
    if (getDataSource() === 'demo') {
      return mockFinancialSummary;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/financial-summary`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch financial summary');
    return response.json();
  }

  async getChartSummary(): Promise<ChartSummaryDto> {
    if (getDataSource() === 'demo') {
      return mockChartSummary;
    }
    const headers = await this.buildHeaders();
    const response = await fetch(
      `${this.getBaseUrl()}/api/dashboard/financial/chart-summary`,
      { headers }
    );
    if (!response.ok) throw new Error('Failed to fetch chart summary');
    return response.json();
  }
}
